import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card, CardContent, Typography, Box, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Grid
} from '@mui/material';
import {
  Refresh, PlayArrow, Stop, Apps as AppsIcon, CheckCircle, Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { CardTitle, BodyText, SuccessButton, DangerButton, getStatusChipStyles, GlassmorphicCard } from '../styles/sharedStyles';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.005))',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: '24px',
  boxShadow: `
    0 8px 32px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.05)
  `,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '16px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  '& .MuiTableHead-root': {
    '& .MuiTableCell-head': {
      background: 'rgba(255, 255, 255, 0.05)',
      color: '#ffffff',
      fontWeight: 600,
      fontSize: '0.875rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.02)',
      },
      '& .MuiTableCell-body': {
        color: '#ffffff',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        fontSize: '0.875rem',
      },
    },
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status'
})(({ status }) => getStatusChipStyles(status));
const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'colorVariant'
})(({ colorVariant }) => ({
  minWidth: '80px',
  height: '32px',
  fontSize: '0.75rem',
  fontWeight: 600,
  borderRadius: '16px',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  ...(colorVariant === 'start' && {
    background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(0, 212, 170, 0.1))',
    border: '1px solid rgba(0, 255, 136, 0.3)',
    color: '#00ff88',
    '&:hover': {
      background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.2), rgba(0, 212, 170, 0.2))',
      border: '1px solid rgba(0, 255, 136, 0.5)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 255, 136, 0.2)',
    },
  }),
  ...(colorVariant === 'stop' && {
    background: 'linear-gradient(135deg, rgba(255, 68, 68, 0.1), rgba(255, 107, 107, 0.1))',
    border: '1px solid rgba(255, 68, 68, 0.3)',
    color: '#ff4444',
    '&:hover': {
      background: 'linear-gradient(135deg, rgba(255, 68, 68, 0.2), rgba(255, 107, 107, 0.2))',
      border: '1px solid rgba(255, 68, 68, 0.5)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(255, 68, 68, 0.2)',
    },
  }),
}));

const AppCard = styled(GlassmorphicCard, {
  shouldForwardProp: (prop) => prop !== 'isRunning'
})(({ isRunning }) => {
  const borderColor = isRunning ? 'rgba(0, 255, 136, 0.25)' : 'rgba(255, 68, 68, 0.25)';

  return {
    minHeight: '100%',
    border: `1px solid ${borderColor}`,
    borderRadius: '20px',
    padding: '22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    justifyContent: 'space-between',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 16px 30px rgba(0, 0, 0, 0.35)'
    }
  };
});

const AppIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isRunning'
})(({ isRunning }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '16px',
  border: `1px solid ${isRunning ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 68, 68, 0.3)'}`,
  background: isRunning
    ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.08), rgba(0, 212, 170, 0.08))'
    : 'linear-gradient(135deg, rgba(255, 68, 68, 0.08), rgba(255, 107, 107, 0.08))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: isRunning ? '0 8px 20px rgba(0, 255, 136, 0.15)' : '0 8px 20px rgba(255, 68, 68, 0.15)'
}));

const APP_ICONS = {
  immich: '/immich.png',
  jellyfin: '/jellyfin.png',
  nextcloud: '/nextcloud.png',
  webdav: '/webdav.png',
  truenas: '/truenas_logo.png',
  'docker-moby': '/docker-moby.png',
  dockge: '/dockge.png',
  filebrowser: '/filebrowser.png',
  glances: '/glances-light.png',
  jellystat: '/jellystat.png',
  wordpress: '/wordpress.png',
  'ddns-updater': '/ddns-updater.png',
  '2fauth-light': '/2fauth-light.png',
  'twofactor-auth': '/2fauth-light.png',
  '2fauth': '/2fauth-light.png',
  ddns: '/ddns-updater.png',
  gramps: '/gramps-web.png'
};

const normalizeIconKey = (value = '') => value.toLowerCase().replace(/[^a-z0-9-]/g, '');

const getAppIcon = (app) => {
  const identifier = normalizeIconKey(app?.name || app?.display_name || '');
  if (APP_ICONS[identifier]) {
    return APP_ICONS[identifier];
  }

  const fallback = Object.keys(APP_ICONS).find((key) => identifier.includes(key));
  return fallback ? APP_ICONS[fallback] : null;
};

const AppsManagement = ({ apiBaseUrl, token, onLogout }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  // Ensure actionLoading is always an object
  const safeSetActionLoading = (updater) => {
    setActionLoading(prev => {
      const prevState = typeof prev === 'object' && prev !== null ? prev : {};
      if (typeof updater === 'function') {
        return updater(prevState);
      }
      return updater;
    });
  };
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchApps = useCallback(async (showRefreshing = false) => {
    try {
      console.log('🔧 AppsManagement: Fetching apps from', `${apiBaseUrl}/api/apps`);
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await axios.get(`${apiBaseUrl}/api/apps`, {
        timeout: 15000,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📥 AppsManagement: Response status:', response.status);

      const data = response.data;
      console.log('📦 AppsManagement: Received data:', data);
      
      // Handle the new status object structure from backend
      const processedApps = (data.apps || []).map(app => ({
        ...app,
        status: app.status?.state || app.status || 'Unknown'
      }));
      
      setApps(processedApps);
      console.log('✅ AppsManagement: Set apps to:', processedApps);
    } catch (err) {
      console.error('❌ AppsManagement: Error fetching apps:', err);
      if (err.response?.status === 401) {
        // Only trigger logout if we actually have a token and it's invalid
        if (token && onLogout) onLogout();
        setError('Session expired. Please log in again.');
      } else {
        setError('TrueNAS Busy - Try Again');
      }
      setApps([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiBaseUrl, token, onLogout]);

  // All hooks must be declared before any conditional logic
  useEffect(() => {
    if (apiBaseUrl !== undefined && apiBaseUrl !== null && token) {
      fetchApps();
    }
  }, [apiBaseUrl, token, fetchApps]);

  // Safety net: ensure actionLoading stays as an object
  useEffect(() => {
    if (typeof actionLoading !== 'object' || actionLoading === null) {
      console.warn('AppsManagement: actionLoading state corrupted, resetting to empty object');
      setActionLoading({});
    }
  }, [actionLoading]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (apiBaseUrl === undefined || apiBaseUrl === null || !token) return;
    const interval = setInterval(() => {
      console.log('🔄 AppsManagement: Auto-refreshing apps...');
      fetchApps(true);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [apiBaseUrl, token, fetchApps]);

  // Defensive check for apiBaseUrl - must come after hooks
  if (apiBaseUrl === undefined || apiBaseUrl === null) {
    console.error('AppsManagement: apiBaseUrl prop is null or undefined');
    return (
      <Box sx={{
        textAlign: 'center',
        py: 4,
        background: 'rgba(255, 255, 255, 0.02)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 68, 68, 0.3)'
      }}>
        <Typography variant="body1" sx={{ color: '#ff4444' }}>
          Configuration error: API base URL not provided
        </Typography>
      </Box>
    );
  }

  const handleAppAction = async (appName, action) => {
    safeSetActionLoading(prev => ({ ...prev, [appName]: true }));
    setError(null);
    setSuccessMessage(null);

    try {
      const endpoint = action === 'start' ? '/api/apps/start' : '/api/apps/stop';
      const response = await axios.post(`${apiBaseUrl}${endpoint}`, {
        name: appName
      }, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = response.data;
      if (result.success) {
        setSuccessMessage(result.message);
        // Refresh the apps list
        await fetchApps();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(result.message || 'TrueNAS Busy - Try Again');
      }
    } catch (err) {
      console.error(`Error ${action}ing app ${appName}:`, err);
      if (err.response?.status === 401) {
        // Only trigger logout if we actually have a token and it's invalid
        if (token && onLogout) onLogout();
        setError('Session expired. Please log in again.');
      } else {
        setError('TrueNAS Busy - Try Again');
      }
    } finally {
      safeSetActionLoading(prev => ({ ...prev, [appName]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'running':
      case 'active':
        return 'success';
      case 'stopped':
      case 'inactive':
        return 'error';
      case 'starting':
      case 'stopping':
        return 'warning';
      default:
        return 'default';
    }
  };

  const isAppRunning = (status) => {
    return status?.toLowerCase() === 'running' || status?.toLowerCase() === 'active';
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={{ xs: 2, sm: 3 }} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <AppsIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: '#00d4ff' }} />
          <Typography variant="h6" sx={{
            fontWeight: 700,
            color: '#ffffff',
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}>
            TrueNAS Apps
          </Typography>
        </Box>
        <Tooltip title="Refresh Apps">
          <IconButton
            onClick={() => fetchApps(true)}
            disabled={refreshing || loading}
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              color: '#00d4ff',
              '&:hover': {
                background: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {refreshing ? (
              <CircularProgress size={20} sx={{ color: '#00d4ff' }} />
            ) : (
              <Refresh sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: '12px',
            background: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid rgba(255, 68, 68, 0.3)',
            color: '#ff4444',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            '& .MuiAlert-icon': {
              color: '#ff4444',
            },
          }}
          icon={<ErrorIcon />}
        >
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            borderRadius: '12px',
            background: 'rgba(0, 255, 136, 0.1)',
            border: '1px solid rgba(0, 255, 136, 0.3)',
            color: '#00ff88',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            '& .MuiAlert-icon': {
              color: '#00ff88',
            },
          }}
          icon={<CheckCircle />}
        >
          {successMessage}
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#00d4ff' }} />
        </Box>
      ) : apps.length > 0 ? (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {apps.map((app) => {
            // Defensive check for app object
            if (!app || typeof app !== 'object') {
              console.error('AppsManagement: Invalid app object:', app);
              return null;
            }

            const appName = app.name || 'Unknown';
            const appStatus = app.status || 'Unknown';
            const isRunning = isAppRunning(appStatus);

            const iconSrc = getAppIcon(app);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={appName}>
                <AppCard isRunning={isRunning}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <AppIcon isRunning={isRunning}>
                          {iconSrc ? (
                            <Box
                              component="img"
                              src={iconSrc}
                              alt={`${appName} icon`}
                              sx={{ width: 28, height: 28, objectFit: 'contain' }}
                            />
                          ) : (
                            <AppsIcon sx={{ color: isRunning ? '#00ff88' : '#ff4444', fontSize: 24 }} />
                          )}
                        </AppIcon>
                        <Typography variant="body1" sx={{
                          fontWeight: 600,
                          color: '#ffffff',
                          textTransform: 'capitalize',
                          fontSize: { xs: '0.95rem', sm: '1rem' }
                        }}>
                          <Box component="span" sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: isRunning ? '#00ff88' : '#ff4444',
                            boxShadow: isRunning ? '0 0 8px rgba(0, 255, 136, 0.5)' : '0 0 8px rgba(255, 68, 68, 0.5)',
                            display: 'inline-flex',
                            mr: 0.5
                          }} />
                          {appName}
                        </Typography>
                      </Box>
                      <StatusChip
                        label={appStatus}
                        status={appStatus}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'row', sm: 'row' } }}>
                    <ActionButton
                      colorVariant="start"
                      variant="outlined"
                      size="small"
                      onClick={() => handleAppAction(appName, 'start')}
                      disabled={(actionLoading && actionLoading[appName]) || isRunning}
                      fullWidth
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {(actionLoading && actionLoading[appName]) ? <CircularProgress size={14} /> : 'Start'}
                    </ActionButton>
                    <ActionButton
                      colorVariant="stop"
                      variant="outlined"
                      size="small"
                      onClick={() => handleAppAction(appName, 'stop')}
                      disabled={(actionLoading && actionLoading[appName]) || !isRunning}
                      fullWidth
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {(actionLoading && actionLoading[appName]) ? <CircularProgress size={14} /> : 'Stop'}
                    </ActionButton>
                  </Box>
                </AppCard>
              </Grid>
            );
          }).filter(Boolean)}
        </Grid>
      ) : (
        <Box sx={{
          textAlign: 'center',
          py: 6,
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.06)'
        }}>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            No apps found or unable to connect to TrueNAS API
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AppsManagement;
